import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { cn } from "cn";
import { useMemo } from "react";
import { Badge } from "@/components/ui/badge/badge";
import { ComboboxMulti, type ComboboxOption } from "@/components/ui/combobox";
import { useGetGlobalOffice } from "@/features/office/_api/use-global-office";

type CategoriesSelectProps = FieldWrapperInjectedProps & {
  className?: string;
  value: string[];
  onChange: (value: string[]) => void;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  disabled?: boolean;
  "data-test"?: string;
};

export default function CategoriesSelect({
  className,
  value,
  onChange,
  defaultOpen = false,
  onOpenChange,
  modal = true,
  disabled,
  ...props
}: CategoriesSelectProps) {
  const { data: globalValues } = useGetGlobalOffice();
  const categories = globalValues?.categories || [];

  const sortedAz = useMemo(() => {
    const collator = new Intl.Collator(undefined, {
      usage: "sort",
      sensitivity: "base",
      numeric: true,
    });
    return [...categories].sort((a, b) => collator.compare(a.title, b.title));
  }, [categories]);

  const options = useMemo<ComboboxOption[]>(
    () =>
      sortedAz.map((category) => ({
        value: category.id,
        label: category.title,
        detail: (
          <span
            className={cn(
              /* sorted */
              "grow",
              "line-clamp-1 break-all text-sm leading-5 font-normal text-text-dark-max",
            )}
          >
            {category.title}
          </span>
        ),
      })),
    [sortedAz],
  );

  return (
    <ComboboxMulti
      {...props}
      value={value}
      onChange={onChange}
      options={options}
      indicator="checkbox"
      groupSelected
      disabled={disabled}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      modal={modal}
      className={className}
      searchPlaceholder="Search categories..."
      emptyText="No category found."
      renderTrigger={(selected) =>
        selected.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {selected.map((option) => (
              <Badge key={option.value} shape="rounded" size="xs">
                {option.label}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-text-dark-min line-clamp-1">
            Select category (ies)
          </p>
        )
      }
    />
  );
}
